/**
 * Sprint C7 — zod-freie Asset-Konstanten + Types.
 *
 * Split aus `asset-types.ts`: die plain `as const`-Arrays + `typeof`-derived
 * Types + pure Result-Types leben hier, damit der Client (FilterBar,
 * LibraryPage, CardActions, …) sie importieren kann OHNE die zod-Runtime
 * (~87 KB minified) ins Entry-Bundle zu ziehen. Die `z.enum`-Schemas leben
 * weiter in `asset-types.ts` und importieren die Arrays von hier.
 *
 * ⚠️ Dieser File MUSS zod-frei bleiben — KEIN `import { z }`, kein `z.`-Usage.
 * Guard-Test in `asset-constants.test.ts`.
 */

export const ASSET_TYPES = ['img', 'video', 'pdf', 'logo'] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

// ── Library Infinite-Scroll + Keyset-Cursor-Pagination (SSOT) ──────────
// LIBRARY_PAGE_SIZE = Rows pro Cursor-Seite (`GET /api/assets`). Server fetcht
// PAGE_SIZE+1 (n+1-Trick: die Extra-Row signalisiert „es gibt eine nächste
// Seite", wird verworfen, nextCursor kodiert die letzte ZURÜCKGEGEBENE Row).
// SEARCH_RESULT_LIMIT = bei aktivem `q` (Suche) ein einziges Limit OHNE Cursor
// (ts_rank-Float-Keyset zu fragil, B1/C5): Suchmengen sind klein; bei >Limit
// Treffern zeigt der Client „Top 500 — Suche verfeinern". PREFETCH_THRESHOLD_ROWS
// = wie viele Virtualizer-REIHEN (nicht Items, C1) vor dem Ende der geladenen
// Range der Prefetch der nächsten Seite triggert.
export const LIBRARY_PAGE_SIZE = 100;
export const SEARCH_RESULT_LIMIT = 500;
export const PREFETCH_THRESHOLD_ROWS = 2;

// ── Upload File-Size-Limit (SSOT) ──────────────────────────────────
// Einheitliches Max pro Asset: 2 GB. EINE Quelle für Server (multipart-
// Parser-Limit + 413-Message) und Client-Dropzone (Pre-Check). Die nginx
// `client_max_body_size` MUSS ≥ diesem Wert + multipart-Overhead sein (Repo-
// Template nginx.conf.example + Live-Server-vhost auf ~2100m), damit am Limit
// die App mit sauberer JSON-Meldung gated statt nginx mit nacktem HTTP 413.
// Verhindert Drift wie 2026-06-08 (nginx stand auf 64M während App+Client 500
// erlaubten → 413 beim 179-MB-Upload).
// ⚠️ 2 GB ist nur memory-safe, weil der Asset-Upload-Handler den File-Part zu
// einer Temp-Datei spoolt (NICHT in RAM buffert) — siehe assets.ts parseMultipart.
export const MAX_FILE_SIZE_MB = 2048;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// In-Memory-Cap für Bild-Verarbeitung (Sharp braucht die Bytes im RAM). Der
// 2-GB-Upload-Spool ist memory-safe für Videos (ffmpeg von Disk) — aber ein
// Bild wird für Thumbnail/Preview/Dims komplett in einen Buffer gelesen. Ohne
// Cap würde ein 2-GB-PNG × Concurrency den Heap sprengen (Codex secfix-review).
// 512 MB spiegelt das frühere Upload-Limit (pre-2GB war ALLES in-RAM bei 500 MB,
// Semaphore 3 → 1.5 GB peak) → kein Regress für reale Bilder. Größere Bilder
// (extrem selten) bekommen einen Placeholder-Thumb statt OOM (best-effort).
export const IMAGE_INMEMORY_MAX_BYTES = 512 * 1024 * 1024;

// ── Video-Stream-Transcoding Schwelle (SSOT) ───────────────────────
// Videos über dieser Größe bekommen asynchron eine web-optimierte Stream-
// Rendition (transcode-worker). Kleinere Videos streamen das Original direkt
// (kein Transcode → 'skipped') — billiger Größen-Proxy für „lädt eh schnell".
export const STREAM_TRANSCODE_MIN_BYTES = 25 * 1024 * 1024;

/**
 * Initialer stream_status für einen frischen Upload (pure, testbar):
 * - Nicht-Video → null (nicht-anwendbar)
 * - Video > Schwelle → 'pending' (Worker transkodiert)
 * - Video ≤ Schwelle → 'skipped' (Original streamt direkt)
 */
export function streamStatusForUpload(
  assetType: AssetType,
  fileSize: number,
): 'pending' | 'skipped' | null {
  if (assetType !== 'video') return null;
  return fileSize > STREAM_TRANSCODE_MIN_BYTES ? 'pending' : 'skipped';
}

// ── Video-Stream-Transcode Retry-Backoff (SSOT) ────────────────────
// MAX_STREAM_RETRIES = Schwelle für `stream_attempts` im Claim (`attempts < MAX`):
// max. 3 FEHLVERSUCHE insgesamt, dann bleibt der Job terminal 'failed'. Der Worker
// zählt nur echte Fehler (markFailed), nicht Claims/Reclaims.
export const MAX_STREAM_RETRIES = 3;

// Backoff pro Fehlversuch (ms): nach dem 1. Fehler 2 min, ab dem 2. 10 min (Cap).
// Index = clamp(attempts, 1, len) − 1 → nie undefined/NaN, auch bei attempts 0/>len.
const BACKOFF_MS = [120_000, 600_000] as const;

/**
 * Backoff in ms vor dem nächsten Retry-Claim, basierend auf der NEUEN Fehlerzahl
 * (pure, testbar). `streamRetryBackoffMs(1) === 120_000`, `(2) === 600_000`,
 * `(3) === 600_000` (Cap), `(0) === 120_000` (defensiv). Caller (`markFailed`)
 * MUSS mit `attempts + 1` rufen (Backoff für den gerade gezählten Fehler).
 */
export function streamRetryBackoffMs(attempts: number): number {
  const idx = Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1;
  return BACKOFF_MS[idx];
}

/**
 * Der S3-Key der Stream-Rendition NUR wenn sie fertig ist — sonst null (pure,
 * testbar). Der Caller signt den Key (`signMediaUrl`). Nur bei `done` + Key
 * vorhanden; `pending`/`processing`/`failed`/`skipped`/null → null → Detail-View
 * fällt auf das Original zurück.
 */
export function streamKeyIfReady(
  streamStatus: string | null,
  streamKey: string | null,
): string | null {
  return streamStatus === 'done' && streamKey ? streamKey : null;
}

/**
 * Transcode läuft noch (pure, testbar) — nicht-terminaler Stream-Status:
 * `pending`/`processing` ODER `failed` mit verbleibenden Retries
 * (`stream_attempts < MAX_STREAM_RETRIES` → der Worker re-claimt). In allen drei
 * Fällen lohnt sich Polling + ein „wird erstellt"-Hint. SSOT für Badge + Poll.
 */
export function isStreamProcessing(
  streamStatus: string | null,
  streamAttempts: number,
): boolean {
  return (
    streamStatus === 'pending' ||
    streamStatus === 'processing' ||
    (streamStatus === 'failed' && streamAttempts < MAX_STREAM_RETRIES)
  );
}

/**
 * Transcode endgültig gescheitert (pure, testbar): `failed` UND alle Retries
 * verbraucht (`stream_attempts >= MAX_STREAM_RETRIES`). Erst dann zeigt das UI
 * „fehlgeschlagen" und das Polling stoppt — vorher re-claimt der Worker den Job,
 * `failed` ist also transient.
 */
export function isStreamFailedTerminal(
  streamStatus: string | null,
  streamAttempts: number,
): boolean {
  return streamStatus === 'failed' && streamAttempts >= MAX_STREAM_RETRIES;
}

// SVG-Asset-Sanitize-Cap (secfix SVG-XSS 2026-06-10): SVG-Uploads laufen vor
// Storage durch sanitizeSvg (jsdom+DOMPurify). Der byte-cap bounded die Parse-
// DoS-Surface. Der Brand-Logo-Default (1MB) wäre fürs allgemeine Asset (Contract
// 500MB) eine Überraschungs-Regression → eigenes, generöseres Cap: 8MB deckt jede
// realistische Vektorgrafik (komplexe Maps/Illustrationen liegen i.d.R. < wenige MB)
// und hält den jsdom-Parse gebounded weit unter dem Raster/Video-Limit.
export const ASSET_SVG_SANITIZE_MAX_BYTES = 8 * 1024 * 1024;

// ── Asset-Lifecycle Status (Sprint Quick-Wins Item 2) ──────────────
// AssetStatus = lifecycle states (PATCH /:id/status body, DB column).
// AssetStatusFilter = AssetStatus + 'all' (GET /api/assets?status= query-param).
// Bewusste Trennung (Codex C2): PATCH rejected 'all', Filter accepts 'all'.
export const ASSET_STATUSES = ['draft', 'approved', 'archived'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

// Deutsche Display-Labels für den Status — SSOT (StatusDropdown editierbar,
// AssetInspector read-only-Anzeige für Nicht-Owner).
export const STATUS_LABELS: Record<AssetStatus, string> = {
  draft: 'Entwurf',
  approved: 'Aktiv',
  archived: 'Archiviert',
};

// Bulk-Skip-Reason (Owner-Edit-Gate, todo:245): wenn ein Member ein fremdes
// Asset bulk-bearbeitet, landet es mit GENAU dieser Fehlermeldung in failed[].
// SSOT, damit Server (partitionEditable) und Client (Bulk-Dialoge, „übersprungen"-
// Toast) auf denselben String matchen — kein fragiles Copy-Paste.
export const FOREIGN_UPLOAD_SKIP_ERROR = 'Kein Schreibrecht (fremder Upload)';

export const ASSET_STATUS_FILTERS = ['draft', 'approved', 'archived', 'all'] as const;
export type AssetStatusFilter = (typeof ASSET_STATUS_FILTERS)[number];

// ── Download-Presets (Sprint Quick-Wins Item 3) ────────────────────
// Interne Preset-Keys (web/print) bleiben stabil — sie sind der Query-Param
// (?preset=web), KEIN S3-Key (Presets werden on-the-fly gestreamt). Die
// Anzeige-Labels (Original/Klein/Mittel, DownloadFormatPicker) UND der Download-
// Dateiname-Suffix (_klein/_mittel, PRESET_CONFIG in routes/assets.ts) sind
// die user-sichtbaren Werte und müssen kohärent bleiben — „Web/Print" war als
// Label irreführend (keine echte Druckqualität ohne Farbprofil) und der Suffix
// passte nach der Label-Umbenennung (#151) nicht mehr (Fix 2026-06-12).
export const DOWNLOAD_PRESETS = ['original', 'web', 'print'] as const;
export type DownloadPreset = (typeof DOWNLOAD_PRESETS)[number];

// Längste-Kante-Px je Downscale-Preset (Sharp fit:inside, withoutEnlargement).
// SSOT für Server-Resize (PRESET_CONFIG in routes/assets.ts) + Client-Auflösungs-
// anzeige im DownloadFormatPicker.
export const DOWNLOAD_PRESET_PX: Record<'web' | 'print', number> = {
  web: 1280,
  print: 3000,
};

// ── Search-Suggest Result-Types (Sprint Quick-Wins Item 1) ─────────
// Server-Response-Shape für GET /api/assets/search/suggest. Caps 5/3/2.
export type SuggestFilename = { id: string; displayFilename: string };
export type SuggestTag = { name: string; count: number };
export type SuggestUploader = { id: string; displayName: string };
export type SuggestResult = {
  filenames: SuggestFilename[];
  tags: SuggestTag[];
  uploaders: SuggestUploader[];
};

/**
 * Bulk-Present (#185-Follow-up): die in einer Bulk-Auswahl tatsächlich
 * vorhandenen Tags/Produkte/Verwendungen + Count (lebende gewählte Assets je
 * Item). Speist den Remove-Picker (Checkliste). Client-konsumiert → zod-frei hier.
 */
export type BulkPresentItem = { id: string; name: string; count: number };
export type BulkPresentResponse = {
  tags: BulkPresentItem[];
  produkte: BulkPresentItem[];
  verwendungen: BulkPresentItem[];
};

/**
 * Bulk-Remove-Undo (#185-Follow-up): die exakt entfernten (asset,item)-Paare,
 * die die Remove-Endpoints via RETURNING zurückgeben + die Restore-Endpoints
 * pair-genau wieder einfügen. Client-konsumiert (Undo-Closure) → zod-frei hier.
 */
export type AssetTagPair = { assetId: string; tagId: string };
export type AssetProduktPair = { assetId: string; produktId: string };
export type AssetVerwendungPair = { assetId: string; verwendungId: string };
